import React, { useEffect, useMemo, memo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  createSession,
  getDeliveryId,
  getDeliveriesFormError,
  getDeliveriesFormLoading,
  getDeliveriesFormData,
  getVisibleFormData,
} from './selectors'
import {
  DefaultJsonFormContextProps,
} from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getDeliveriesEnums, getDeliveriesForm } from '../../../store/deliveries/action-creators'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import DeliveriesTabs from './tabs/deliveries-tabs'
import { DeliveriesFormsHeader } from './deliveries-forms/deliveries-form-header/deliveries-form-header'
import { setDeliveriesFormData } from '../../../store/deliveries/actions'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { useSaveResult } from './hooks/useSaveResult'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { Modal, message, Result, Skeleton } from 'antd'

function AdminNewObjectPage() {
  const { id } = useParams<{ id: string }>()
  if (!id) return null

  const pageTitle = useMemo(
    () => id === 'new' ? 'Новая доставка' : `Доставка № ${id}`,
    [id],
  )

  const breadcrumbs = useMemo(() => [
    {
      href: '/logistics/deliveries',
      title: 'Доставки',
    },
    {
      title: pageTitle,
    },
  ], [pageTitle])

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const deliveryId = useSelector(getDeliveryId)
  const entityType = 'delivery'
  const deliveriesFormIsLoading = useSelector(getDeliveriesFormLoading)
  const deliveriesFormError = useSelector(getDeliveriesFormError)
  const deliveriesFormData = useSelector(getDeliveriesFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const { sessionData } = useSelector(createSession)
  const isShowByAccess = sessionData.roles.includes(19)
  const { saveResult } = useSaveResult()

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }), [id])

  useEffect(() => {
    let mounted = true
    if (mounted) {
      const getInitPage = async (id: string) => {
        dispatch(setDeliveriesFormData({}))
        dispatch(getDeliveriesEnums(id))
        dispatch(getDeliveriesForm(id))
      }
      getInitPage(id).catch(console.error)
    }
    return () => {
      mounted = false
    }
  }, [id, isShowByAccess, dispatch])

  useEffect(() => {
    if (id === 'new' && deliveryId) {
      navigate({
        pathname: `/logistics/deliveries/${deliveryId}`,
      })
    }
    return () => {
    }
  }, [id, deliveryId, navigate])

  const handleToOrder = useCallback(async () => {
    const res: any = await saveResult(id, visibleFormData, deliveriesFormData?.stages?.current, true, undefined)

    if (res?.shiftResult?.shifted && res?.shiftResult?.shifted.length > 0) {
      Modal.info({
        title: 'Перенос очереди',
        content: (
          <div>
            {res.shiftResult.shifted.map((order) => (
              <div key={order.id}>
                <a
                  href={`/logistics/deliveries/${order.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <b>{order.order_reference}</b>
                </a>
                : перенесён на очередь {order.newSequence}
                {res.shiftResult.unapproved.some((u) => u.id === order.id) && (
                  <span>,<b> снята отметка о согласовании</b></span>
                )}
              </div>
            ))}
          </div>
        ),
        okText: 'ОК',
        width: 600,
      })
    }
  }, [id, saveResult, visibleFormData, deliveriesFormData])

  const handleExportToSchedex = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/schedex/orders/export', {
        method: 'POST',
        body: JSON.stringify({ orders: [Number(id)] }),
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.success) {
        if (response.result.failed_orders.length > 0) {
          const errorMessages = response.result.failed_orders.map(order => (
            order.errors.join(', ')
          ))

          Modal.error({
            title: 'Ошибки при выгрузке',
            content: <div>{errorMessages}</div>,
            onOk() {},
          })
        } else {
          message.success('Заказ успешно выгружен в Шедекс')
        }
      } else {
        message.error(response.error || 'Ошибка отправки в Шедекс', 5)
      }
    } catch (error) {
      console.error('Ошибка отправки:', error)
      message.error('Ошибка отправки в Шедекс: ' + error)
    }
  }, [id])

  const handleUnloadFromSchedex = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/schedex/orders/import', {
        method: 'POST',
        body: JSON.stringify({ orders: [Number(id)], update_fields: true }),
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.success) {
        if (response.result.failed_orders.length > 0) {
          const errorMessages = response.result.failed_orders.map(order => (
            order.errors.join(', ')
          ))

          Modal.error({
            title: 'Ошибки при выгрузке',
            content: <div>{errorMessages}</div>,
            onOk() {},
          })
        } else {
          message.success('Заказ успешно выгружен из Шедекс')
        }
      } else {
        message.error(response.error || 'Ошибка выгрузки из Шедекс', 5)
      }
    } catch (error) {
      console.error('Ошибка выгрузки:', error)
      message.error('Ошибка выгрузки из Шедекс')
    }
  }, [id])

  const handleClone = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/logistics/deliveries/clone', {
        method: 'POST',
        body: JSON.stringify({ orders: [Number(id)] }),
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.success && response.order_id) {
        navigate(`/logistics/deliveries/${response.order_id}`)
      } else {
        message.error(response.error || 'Ошибка клонирования заказа', 5)
      }
    } catch (error) {
      console.error('Ошибка:', error)
    }
  }, [id, navigate])

  const handleReturnStage = useCallback(async () => {
    dispatch(getDeliveriesForm(id))
  }, [id, dispatch])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {deliveriesFormIsLoading && <Skeleton active/>}
      {!deliveriesFormIsLoading && deliveriesFormError &&
        <Result
          status={typeof deliveriesFormError === 'object' ? deliveriesFormError.statusCode : 'error'}
          title='Ошибка'
          subTitle={
            typeof deliveriesFormError === 'object' ? deliveriesFormError.message : deliveriesFormError
          }
        />
      }
      {!deliveriesFormIsLoading && !deliveriesFormError &&
        <DefaultJsonFormContext.Provider value={defaultProps}>
          <DeliveriesFormsHeader
            id={id}
            stage={deliveriesFormData?.stages?.current}
            stages={deliveriesFormData?.stages?.items}
            toOrder={handleToOrder}
            onExportToSchedex={handleExportToSchedex}
            onUnloadFromSchedex={handleUnloadFromSchedex}
            onClone={handleClone}
            onReturnStage={handleReturnStage}
            showTitle={false}
          />
          <DeliveriesTabs id={id} />
        </DefaultJsonFormContext.Provider>
      }
    </B2BLayout>
  )
}

export default memo(AdminNewObjectPage)
